/**
 * SuggestedQuestionsWidget.tsx - Suggested Questions Widget
 *
 * Welcome-page widget, split into two parts so the empty state can place them
 * around the centered input:
 *   - SuggestedQuestionsHeader: book cover + title + description (vertical, centered)
 *   - SuggestedQuestionChips: question chips wrapped and centered
 */

import { Button, Flex, Image, Text, VStack } from '@chakra-ui/react';
import { SuggestedQuestion, useSuggestedQuestions } from '@/hooks/useSuggestedQuestions';
import { useChatContext } from '@/contexts/ChatContext';
import { useFilterContext } from '@/contexts/FilterContext';
import { dateRangeForQuestion } from '@/utilities/questionDateRange';
import { isRTLText } from '@/utilities/isRTLText';
import { useTranslation } from 'react-i18next';

const useWidgetData = () => {
    const { parentCircleId } = useChatContext();
    const data = useSuggestedQuestions(parentCircleId || null);
    const { i18n } = useTranslation();
    const uiIsRTL = i18n.dir() === 'rtl';
    const contentIsRTL = isRTLText(data.title, data.description, data.questions[0]?.text);
    return { ...data, isRTL: uiIsRTL || contentIsRTL };
};

/** Book cover + title + description, stacked and centered. */
export const SuggestedQuestionsHeader: React.FC = () => {
    const { title, description, image, isLoading, isRTL } = useWidgetData();

    if (isLoading || !(image || title || description)) {
        return null;
    }

    return (
        <VStack
            className="fadeLong"
            gap={{ base: 1.5, md: 2 }}
            alignItems="center"
            width="100%"
            maxWidth="540px"
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {image && (
                <Image
                    src={image}
                    alt={title || 'Widget image'}
                    maxHeight={{ base: '90px', md: '120px' }}
                    maxWidth="100%"
                    objectFit="cover"
                    borderRadius="var(--cair-radius-md)"
                />
            )}
            {title && (
                <Text
                    dir="auto"
                    fontSize={{ base: 'sm', md: 'md' }}
                    fontWeight="semibold"
                    color="var(--cair-text)"
                    textAlign="center"
                    width="100%"
                    lineHeight="1.3"
                >
                    {title}
                </Text>
            )}
            {description && (
                <Text
                    dir="auto"
                    fontSize={{ base: 'xs', md: 'sm' }}
                    color="var(--cair-text-secondary)"
                    textAlign="center"
                    width="100%"
                    lineHeight="1.3"
                >
                    {description}
                </Text>
            )}
        </VStack>
    );
};

/** Question chips, wrapped and centered. */
export const SuggestedQuestionChips: React.FC = () => {
    const { setPendingQuestion, loading } = useChatContext();
    const { updateDateRange } = useFilterContext();
    const { questions, isLoading, isRTL } = useWidgetData();

    if (isLoading || questions.length === 0) {
        return null;
    }

    const handleClick = (question: SuggestedQuestion) => {
        if (loading) return;
        // Apply the admin-configured date filter so it shows in the filter panel
        // and is picked up by activeFilters on send. Always call this, even with
        // no date spec — it has to clear a range left by a previously tapped chip.
        updateDateRange(dateRangeForQuestion(question.dateSpec));
        setPendingQuestion(question.text);
    };

    return (
        <Flex
            className="fadeLong"
            width="100%"
            wrap="wrap"
            justifyContent="center"
            alignItems="center"
            gap={{ base: 1.5, md: 2 }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {questions.map((question, index) => (
                <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleClick(question)}
                    disabled={loading}
                    borderRadius="var(--cair-radius-lg)"
                    borderColor="var(--cair-border)"
                    background="var(--cair-surface-elevated)"
                    color="var(--cair-text)"
                    fontWeight="normal"
                    fontSize={{ base: 'sm', md: 'md' }}
                    height="auto"
                    maxWidth="100%"
                    paddingX={{ base: 3, md: 4 }}
                    paddingY={{ base: 2, md: 2.5 }}
                    whiteSpace="normal"
                    wordBreak="break-word"
                    textAlign={isRTL ? 'right' : 'left'}
                    lineHeight="1.3"
                    _hover={{
                        background: 'var(--cair-accent-subtle)',
                        borderColor: 'var(--cair-accent)',
                    }}
                >
                    {question.text}
                </Button>
            ))}
        </Flex>
    );
};
